import { useCallback, useState } from 'react';

/**
 * A number remembered in this browser — a pane width, a split. A convenience
 * only: storage can be blocked or cleared at any time, and the fallback is
 * always just the default.
 */
export function useStoredNumber(
  key: string,
  fallback: number,
  clamp: (value: number) => number = (value) => value,
): readonly [number, (value: number) => void, (value: number) => void] {
  const [value, setValue] = useState<number>(() => {
    try {
      const stored = window.localStorage.getItem(key);
      const parsed = stored === null ? Number.NaN : Number(stored);
      return Number.isFinite(parsed) ? clamp(parsed) : fallback;
    } catch {
      return fallback;
    }
  });

  /** Save where it ended up — once per drag, not per pixel. */
  const commit = useCallback(
    (next: number) => {
      setValue(next);
      try {
        window.localStorage.setItem(key, String(next));
      } catch {
        // Not remembered this time; the pane still moved.
      }
    },
    [key],
  );

  return [value, setValue, commit] as const;
}
